import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import UserRolesDialog from "../../components/Users/UserRolesDialog/index.js";
import AdminFormDialog from "../../components/Users/AdminFormDialog/index.js";
import DeliveryFormDialog from "../../components/Users/DeliveryFormDialog/index.js";
import AgencyFormDialog from "../../components/Users/AgencyFormDialog/index.js";
import styles from "./usersStyles.js";
import {
  Container,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { getUsers, getUsersBySuperadmin } from "../../api/userApi";
import { getZones } from "../../api/zonesApi";
import { notification } from "../../utils/Notifications";
import UsersTable from "../../components/Users/UsersTable";
import Title from "../../components/SharedComponents/Title";
import SearchBar from "../../components/SharedComponents/SearchBar";
import useLogout from "../../hooks/auth/authHook.js";
import { usePermissions } from "../../hooks/permissions/usePermissions";

const Users = () => {
  const [logout] = useLogout();
  const { hasAllPermissions, hasAnyPermission } = usePermissions();
  const { extraInformation } = useSelector((state) => state.user);
  const isSuperAdmin = extraInformation?.id_role === 5;

  const [users, setUsers] = useState([]);
  const [zones, setZones] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedZone, setSelectedZone] = useState(null); // null = "Todas las zonas"
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(null);
  const hasFetchedInitial = useRef(false);

  const handleDialogOpen = (dialogName) => {
    setOpenDialog(dialogName);
  };

  const handleDialogClose = () => {
    setOpenDialog(null);
  };

  const fetchUsers = () => {
    setIsLoading(true);

    const fetchFunction = isSuperAdmin ? getUsersBySuperadmin : getUsers;
    const fetchParams = isSuperAdmin
      ? selectedZone
        ? [Number(selectedZone)]
        : null
      : undefined;

    fetchFunction(fetchParams)
      .then((response) => {
        if (response.data.status_Code === 200) {
          setUsers(response.data.users || []);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener los usuarios",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchZones = () => {
    getZones()
      .then((response) => {
        if (response.data.status_Code === 200) {
          setZones(response.data.ls_zones || []);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener las zonas",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      });
  };

  const handleZoneChange = (event) => {
    const zoneValue = event.target.value;
    setSelectedZone(zoneValue === "" ? null : zoneValue);
  };

  useEffect(() => {
    fetchZones();
  }, []);

  useEffect(() => {
    // Solo hacer fetch si ya tenemos el rol del usuario y no hemos hecho fetch antes
    if (extraInformation?.id_role !== undefined && !hasFetchedInitial.current) {
      hasFetchedInitial.current = true;
      fetchUsers();
    }
  }, [extraInformation?.id_role]);

  // Actualizar usuarios cuando cambie la zona (solo para SuperAdmin, después del fetch inicial)
  useEffect(() => {
    if (
      isSuperAdmin &&
      extraInformation?.id_role === 5 &&
      hasFetchedInitial.current
    ) {
      fetchUsers();
    }
  }, [selectedZone]);

  return (
    <Container
      style={{ minHeight: "100%", backgroundColor: grey[100], padding: "24px" }}
    >
      <Title title="Usuarios" />
      <SearchBar setSearch={setSearch}>
        {hasAllPermissions(["usr_vw"]) &&
          hasAnyPermission(["usr_cra", "usr_crd", "usr_crg"]) && (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => handleDialogOpen("Rol")}
              style={styles.addUserButton}
            >
              Agregar
            </Button>
          )}

        <UserRolesDialog
          open={openDialog === "Rol"}
          onClose={handleDialogClose}
          onOpenDialog={handleDialogOpen}
        />
        <AdminFormDialog
          open={openDialog === "Administrador"}
          onClose={handleDialogClose}
          fetchUsers={fetchUsers}
          zones={zones}
        />
        <DeliveryFormDialog
          open={openDialog === "Repartidor"}
          onClose={handleDialogClose}
          fetchUsers={fetchUsers}
          zones={zones}
        />
        <AgencyFormDialog
          open={openDialog === "Sucursal"}
          onClose={handleDialogClose}
          fetchUsers={fetchUsers}
          zones={zones}
        />
      </SearchBar>

      {isSuperAdmin && (
        <Container style={{ marginBottom: "16px", marginTop: "16px" }}>
          <FormControl variant="standard" style={{ minWidth: 200 }}>
            <InputLabel id="zone-filter-label">Filtrar por zona</InputLabel>
            <Select
              labelId="zone-filter-label"
              id="zone-filter"
              value={selectedZone || ""}
              onChange={handleZoneChange}
            >
              <MenuItem value="">
                <em>Todas las zonas</em>
              </MenuItem>
              {zones.map((zone) => {
                const zoneId =
                  typeof zone === "string"
                    ? zone
                    : zone.id_zone || zone.id || zone.zone_code;
                const zoneName =
                  typeof zone === "string"
                    ? zone
                    : zone.zone_name || zone.name || zone.zone_code || zone.id;
                return (
                  <MenuItem key={zoneId} value={zoneId}>
                    {zoneName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Container>
      )}

      <UsersTable
        users={users}
        fetchUsers={fetchUsers}
        search={search}
        isLoading={isLoading}
      />
    </Container>
  );
};

export default Users;
